import { Chip } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useState } from 'react'
import { EmailReceiptChips } from '../../components/email-receipt'
import type { CaseStaffReminderOut, CaseStaffRemindersOut } from '../../lib/api/generated'
import { formatOfficeDateTime } from '../../lib/toronto-time'
import { caseStaffRemindersQuery } from './api'
import { bodyText } from './reminders-panel'

/**
 * Staff/contractor reminders that covered this case (#1006). One line per send: who, when, what was asked,
 * View email for the exact text, answer state, and the next chase. Internal only; nothing is written to Desk.
 */

const ANSWER: Record<CaseStaffReminderOut['answer_state'], { label: string; tone: 'warning' | 'success' | 'primary' }> = {
  still_waiting: { label: 'Still waiting', tone: 'warning' },
  form_submitted: { label: 'Form submitted', tone: 'success' },
  reply_received: { label: 'Reply received', tone: 'success' },
}

function stamp(iso: string): string {
  const text = formatOfficeDateTime(iso)
  return text === 'Window not supplied' ? '' : text.replace(',', '')
}

export function reminderLine(row: CaseStaffReminderOut): string {
  const verb = row.status === 'drafted' ? 'Reminder drafted for' : 'Reminder sent to'
  const asked = row.asked ? `asked: ${row.asked}` : ''
  return [`${verb} ${row.recipient_name}`, stamp(row.at), asked].filter(Boolean).join(' · ')
}

function ReminderRow({ row }: { row: CaseStaffReminderOut }) {
  const [open, setOpen] = useState(false)
  const answer = ANSWER[row.answer_state]
  const chase = row.next_chase_at ? stamp(row.next_chase_at) : ''
  return (
    <li className="text-xs" data-status={row.status} data-answer={row.answer_state}>
      <p>
        {reminderLine(row)}
        {row.status === 'drafted' ? (
          <Chip tone="primary" size="sm" className="ml-1.5">
            drafted
          </Chip>
        ) : null}
      </p>
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        <button type="button" className="cursor-pointer font-medium text-primary" onClick={() => setOpen((value) => !value)}>
          {open ? 'Hide email' : 'View email'}
        </button>
        <Chip tone={answer.tone} size="sm">
          {answer.label}
        </Chip>
        {row.status === 'sent' ? <EmailReceiptChips receipt={row.receipt} /> : null}
        {chase ? <span className="text-subtle">next chase {chase}</span> : null}
      </div>
      {open ? <EmailBody row={row} /> : null}
    </li>
  )
}

function EmailBody({ row }: { row: CaseStaffReminderOut }) {
  const text = row.body_text || (row.body ? bodyText(row.body) : '')
  if (!text) return <p className="text-xs text-muted-foreground">The stored email has no body.</p>
  return (
    <pre className="mt-1 max-h-52 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/60 p-2 font-sans text-xs leading-5">
      {row.subject ? `${row.subject}\n\n${text}` : text}
    </pre>
  )
}

export function StaffReminderTrail({ caseId }: { caseId: string }) {
  const q = useQuery(caseStaffRemindersQuery(caseId))
  if (q.isPending) return null
  if (q.isError) {
    return <p className="text-xs text-muted-foreground">Couldn't read the reminders for this case.</p>
  }
  const rows = q.data.rows ?? []
  if (!rows.length) return null
  return (
    <section
      aria-label="Staff reminders"
      className="mx-5 mt-3 space-y-1 rounded-md border border-dashed border-border px-3 py-2"
      data-testid="staff-reminder-trail"
    >
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Bell className="size-3.5" aria-hidden />
        Reminders sent
      </div>
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <ReminderRow key={row.event_id} row={row} />
        ))}
      </ul>
    </section>
  )
}

export function emptyTrail(data: CaseStaffRemindersOut | undefined): boolean {
  return !data?.rows?.length
}
